import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Group, Box, SimpleGrid, Title, Stack,ScrollArea, Text, Accordion, ActionIcon, Divider, Center, Textarea, Button, Flex, Grid, Rating, Collapse, Anchor, Card, Tooltip, Skeleton, TextInput } from "@mantine/core";
import { IconArrowDown, IconArrowRight, IconChartBar, IconCircleArrowRight, IconEye, IconFileText, IconInfoCircle, IconLock, IconTarget, IconTrendingDown, IconTrendingUp, IconX } from "@tabler/icons-react";
import { saveAssessment, saveAttribute, deleteAttribute, deleteAssessment } from "../actions/evaluationActions";
import { ATTRIBUTE_TYPES } from "../enums/AttributesTypes";
import ModalAssessment from "../components/Evaluation/ModalAssessment.js";
import TitleWithDescription from "../components/TitleWithDescription";
import { notifyError } from "../components/Toast";
import excelRubrica from '../assets/Rúbrica Etapas 2 y 3. Versión Final.xlsx'
import useActivePermission from "../hooks/useActivePermission.js";
import ModalEvidence from "../components/ModalEvidence.js";
import equals from "../utils/equals.js";

const normalize = (s) => (s ?? "").toString().trim().toUpperCase();
const ugKey = (carrera, sede) => `${normalize(carrera)}|${normalize(sede)}`;

const initialAttr = { tipo: null, titulo: "", descripcion: "" };

const AssessmentPage = ({
  usedAtribbutes,
  isFacultyUnit,
  navigateTo,
  editionEnabled = false,
}) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { data: evaluation, loading: loadingEvaluation } = useSelector((state) => state.evaluation.evaluation);
  const { data: pei, loading: loadingPei } = useSelector((state) => state.pei.matrixPei);
  const { data: carreras, loading: loadingCarreras} = useSelector((state) => state.academic);
  
  const {data: evidences} = useSelector((state) => state.evidence);
  const {assessments, attributes} = useSelector((state) => state.evaluation);
  
  const activePermission = useActivePermission();

  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [evidenceModalOpened, setEvidenceModalOpened] = useState(false);
  const [accordionOpened, setAccordionOpened] = useState(false);
  const [assessmentOpened, setAssessmentOpened] = useState(false);
  const [assessmentDataSelected, setAssessmentDataSelected] = useState({})
  const [attribute, setAttribute] = useState(initialAttr); 
  
  const [downloadingFile, setDownloadingFile] = useState(false); 
  const isBootstrapping = loadingEvaluation || loadingPei || loadingCarreras;
  const isReady = !!evaluation && !!pei && Array.isArray(carreras) && !!evaluation?.criterio;

  const criterioData = useMemo(() => {
    if (!isReady) return null;

    const criterioId = evaluation.criterio?.Id;
    const numCriterio = evaluation.criterio?.numCriterio;
    if (!criterioId && !numCriterio) return null;

    for (const obj of (pei?.Objetivos ?? [])) {
      const criterio = obj.criterios?.find(
        (c) => c.Id === criterioId || (numCriterio && c.numCriterio === numCriterio)
      );
      if (criterio) {
        return {
          objetivo: obj,
          criterio,
          kpis: criterio.kpiList ?? [],
        };
      }
    }
    return null;
  }, [isReady, pei, evaluation]);

  const objetivo = criterioData?.objetivo ?? null;
  const criterio = criterioData?.criterio ?? null;
  const kpis     = criterioData?.kpis     ?? [];

  const kpiIdSet = useMemo(() => {
    if (!criterioData || kpis.length === 0) return new Set();
    return new Set(kpis.map(k => normalize(k.Id)));
  }, [criterioData, kpis]);

  const evidencesByUG = useMemo(() => {
    if (!criterioData || kpiIdSet.size === 0) return {};
    const out = {};
    (evidences ?? []).forEach(ev => {
      const hasActiveKpi = (ev.kpis ?? []).some(k => kpiIdSet.has(normalize(k.Id)));
      if (!hasActiveKpi) return;
      (ev.unidadesGestion ?? []).forEach(ug => {
        const key = ugKey(ug.codigoUnidadGestion, ug.codigoSede);
        (out[key] ||= []).push(ev);
      });
    });
    return out;
  }, [criterioData, evidences, kpiIdSet]);

  const assessByUG = useMemo(() => {
    const out = {};
    (evaluation?.valoraciones ?? []).forEach(a => {
      out[ugKey(a.codigoCarrera, a.codigoSede)] = a;
    });
    return out;
  }, [evaluation?.valoraciones]);

  const allowImprovement = useMemo(() => {
    if (!isReady) return undefined;
    const totalUG = carreras.length;
    const hasAttributes = (evaluation?.cualidades?.length ?? 0) > 0;
    const assessedCount = Object.keys(assessByUG).length;
    const assessmentsCompleted = isFacultyUnit ? (assessedCount >= totalUG) : true;

    return totalUG > 0 && hasAttributes && assessmentsCompleted;
  }, [isReady, carreras, evaluation, assessByUG, isFacultyUnit]);
  
//#region Assessment
  const handleSaveAssessment = (newAssessment) => {
    dispatch(saveAssessment(evaluation?.Id,newAssessment, user)).then(() => {
      setAssessmentOpened(false);
      setAssessmentDataSelected({});
    })
  };

  const handleDeleteAssessment = (assessmentId) => {
    dispatch(deleteAssessment(evaluation?.Id,assessmentId, user)).then(() => {
      setAssessmentOpened(false);
      setAssessmentDataSelected({});
    })
  };

  const handleSelectAssessment = (codCarrera, carrera, sede, assessment, evidences) => {
    const valuedEvidences = evidences.map(ev => ev.Id);
    const data = { 
      carrera: {codigo: codCarrera, descripcion: carrera}, 
      sede: sede, 
      evidenciasValoradas: valuedEvidences,
      assessment: assessment 
    }

    setAssessmentDataSelected(data);
    setAssessmentOpened(true);
  };

  const handleCloseAssessmentModal = () => {
    setAssessmentOpened(false);
    setAssessmentDataSelected({});
  };
//#endregion
//#region Attributes

  const setAttributeField = (k, v) => setAttribute((prev) => ({ ...prev, [k]: v }));

  const toggleAttributeType = (tipo) =>
    setAttribute((prev) =>
      prev.tipo === tipo ? { ...initialAttr } : { ...prev, tipo }
    );

  const handleSaveAttribute = () => {
    if (attributes.saving) return;

    if (!attribute.tipo) {
      notifyError("Debe seleccionar una cualidad (Fortaleza o Debilidad).");
      return;
    }
    if (!normalize(attribute.descripcion)) {
      notifyError("La descripción de la cualidad es requerida.");
      return;
    }

    const payload = {
      tipoAtributo: { Id: attribute.tipo },
      titulo: normalize(attribute.titulo) || null,
      descripcion: normalize(attribute.descripcion),
    };

    dispatch(saveAttribute(evaluation.Id, payload, user))
      .then(() => {
        setAttribute(initialAttr);
      })
  };

  const handleDeleteAttribute = (attributeId) => {
    dispatch(deleteAttribute(attributeId, evaluation.Id, user));
  };
//#endregion
  
  const handleAccordionChange = useCallback((value) => {
    setAccordionOpened(!!value);
  }, []);

  const handleNavigateUnitDetail = (unitId, sedeCod = null) => {
    if(unitId){
      navigate(`/backoffice/evaluation/overview/${unitId}/detail?sede=${sedeCod || ''}  `)
    }
  }

  const handleDownloadRubrica = () => {
    
    setDownloadingFile(true);

    const link = document.createElement('a');
    link.href = excelRubrica;
    //link.download = 'Rúbrica Etapas 2 y 3.xlsx';
    link.target="_blank"
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setDownloadingFile(false);
  }
  
  return (
    <Box>
      <Box >
        <Accordion
          className="accordion-card"
          //value={accordionOpened}
          onChange={handleAccordionChange}
          variant="separated"
          //defaultValue={['objetivo']}
          chevron={<IconArrowDown/>}
          chevronSize={30}
          radius={'md'}
        >
          <Accordion.Item  key={"objetivo"} value={"objetivo"}>
            <Accordion.Control>
              <Text size="lg" fw={600}>Detalles del Criterio</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Divider mb={'xs'}/>
              {!isReady ? (
                <SimpleGrid cols={2}>
                  <Skeleton height={80} width={'95%'} radius="md" />
                  <Skeleton height={80} width={'95%'} radius="md" />
                </SimpleGrid>
              ):(
                <>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Stack gap={'xs'}>
                        <Text size='md' fw={600}><IconTarget stroke={1.5}/>  Objetivo {objetivo?.numObjetivo}</Text>
                        <Text size='sm'>{objetivo?.descripcion}</Text>
                      </Stack>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Stack gap={'xs'}>
                        <Text size='md' fw={600}><IconChartBar stroke={1.5}/>  Criterio {criterio?.numCriterio}</Text>
                        <Text size='sm'>{criterio?.descripcion}</Text>
                      </Stack>
                    </Grid.Col>
                  </Grid>
                  <Grid mt={'lg'}>
                    {kpis?.map((kpi, index)=> (
                      <Grid.Col span={{ base: 12, sm: 6, lg:4 }} key={`${index}`}>
                        <Card withBorder radius="md" p="md" bg="gray.0" h={"100%"}>
                          <Stack gap={5} justify="space-between">
                            <Group wrap="nowrap" gap={'md'}>
                              <Text size='md' fw={600} style={{ whiteSpace: 'nowrap' }}> {kpi.numeroCompleto} </Text>
                              <Text size='md' fw={600}> {kpi.titulo} </Text>
                            </Group>
                            <Text size='sm'>{kpi.descripcion}</Text>
                          </Stack>
                        </Card>
                      </Grid.Col>
                    ))}
                  </Grid>
                </>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Box>
      <Box className="card-base" mt={"md"}>
        <Flex>
          <Box flex={8} pr={"10px"}>
            <Button 
              color="#E72166" 
              onClick={navigateTo} 
              fullWidth
              disabled={!allowImprovement}
              leftSection={allowImprovement ? <IconArrowRight size={20}/> : <IconLock size={20}/>}
            >
              Ir al Plan de Mejoras
            </Button>
            {!allowImprovement && (
              <Center inline mt={'sm'}>
                <IconInfoCircle color="grey" size={40}/>
                <Text ml={'xs'} size="sm" c='dimmed'>
                  El usuario solo podrá completar el plan de mejora de un criterio
                  cuando se hayan finalizado todas las valoraciones de carreras-sede
                  y se haya registrado al menos una debilidad o fortaleza.
                </Text>
              </Center>
            )}
          </Box>
          <Box flex={4} pl={"10px"}>
            <Button
              fullWidth
              variant="default"
              onClick={() => handleDownloadRubrica()}
              loading={downloadingFile}
              leftSection={<IconEye size={20}/>}
            >
              Rubrica del Plan de Mejoras
            </Button>
          </Box>
        </Flex>
        <Flex mt={"xl"}>
          {/* VALORACIONES */}
          <Box flex={8} pr={"10px"} style={{ borderRight: "2px solid #E72166" }}>
            <TitleWithDescription
              title={"Análisis y valoración"}
              description={`Analice y evalúe las unidades de gestión Carreras-Sede en relación con el cumplimiento global del Criterio ${criterio?.numCriterio}. Considere todas las evidencias presentadas y valore la calidad del conjunto para representar y validar de forma objetiva el nivel de cumplimiento alcanzado.`}
              titleOrder={2}
            />
            <Box className="container-pink">
              <SimpleGrid cols={3}>
                <div>
                  <Text ta={"center"} size="md" fw={600}>
                    Carrera-Sede
                  </Text>
                </div>
                <div>
                  <Text ta={"center"} size="md" fw={600}>
                    Indicadores y Evidencias
                  </Text>
                </div>
                {isFacultyUnit && (
                  <div>
                    <Text ta={"center"} size="md" fw={600}>
                      Evaluacion Criterio
                    </Text>
                  </div>
                )}
              </SimpleGrid>
            </Box>
            {!isReady ? (
              <Text align="center" c="gray" mt={"lg"}>
                Cargando Carreras y evidencias ...
              </Text>
            ):(
              <Box>
                {(carreras ?? []).map((car) => {
                  const key = ugKey(car.codCarrera, car.sede);
                  const assessment = assessByUG[key];
                  const evsUG = evidencesByUG[key] || [];

                  const isAssessed = Boolean(assessment);
                  const evsUGVisible = isAssessed
                    ? evsUG.filter(ev => ev?.tieneValoracion === true)
                    : evsUG;

                  return (
                    <Box className="container-pink" key={key}>
                      <Grid>
                        {/* Columna Carrera–Sede */}
                        <Grid.Col span={isFacultyUnit ? 3 : 4}>
                          <Stack justify="center" h="100%">
                            <Box>
                              <Text fw={700} size="xs" c="#E72166">
                                {car.codCarrera} - {car.sede}
                              </Text>
                              <Text fw={600} size="sm">{car.descripcion}</Text>
                            </Box>
                            {isFacultyUnit && (
                              <Anchor c="#3590f3ff" onClick={() => handleNavigateUnitDetail(car.codCarrera, car.sede)}>
                                Ver detalles
                              </Anchor>
                            )}
                          </Stack>
                        </Grid.Col>

                        {/* Columna Evidencias por KPI */}
                        <Grid.Col span={isFacultyUnit ? 6 : 8}>
                          {(kpis ?? []).map((kpi) => {
                            const kpiId = normalize(kpi.Id);
                            const evsForKpi = evsUGVisible.filter(ev =>
                              (ev.kpis ?? []).some(x => normalize(x.Id) === kpiId)
                            );

                            return (
                              <div key={kpi.Id} style={{ marginBottom: 8 }}>
                                <Text fw={500}>{kpi.numKpi}.{kpi.titulo}</Text>

                                {evsForKpi.length === 0 ? (
                                  <Text size="sm" c="dimmed" mt={2}>Sin evidencias cargadas</Text>
                                ) : (
                                  <Group gap="xs" mt={4}>
                                    {evsForKpi.map((ev) => (
                                      <Tooltip label={ev.titulo} position="bottom" withArrow key={ev.Id}>
                                        <ActionIcon
                                          variant="filled"
                                          aria-label="file"
                                          radius="xl"
                                          onClick={() => { setSelectedEvidence(ev); setEvidenceModalOpened(true); }}
                                          color="#E72166"
                                        >
                                          <IconFileText style={{ height: '70%', width: '70%' }} />
                                        </ActionIcon>
                                      </Tooltip>
                                    ))}
                                  </Group>
                                )}
                              </div>
                            );
                          })}
                        </Grid.Col>

                        {/* Columna Valoración */}
                        {isFacultyUnit && (
                          <Grid.Col span={3}>
                            <Stack justify="center" h="100%">
                              <Group justify="center">
                                {assessment ? (
                                  <Rating value={assessment.puntuacion} count={4} size="lg" readOnly />
                                ) : (
                                  <Text c="dimmed" ta="center">Sin valoración</Text>
                                )}
                              </Group>
                              {editionEnabled && (
                                <Button
                                  color="#E72166"
                                  radius="lg"
                                  size="compact-md"
                                  onClick={() =>
                                    handleSelectAssessment(car.codCarrera, car.descripcion, car.sede, assessment, evsUGVisible)
                                  }
                                >
                                  {assessment ? "Editar" : "Agregar"}
                                </Button>
                              )}
                            </Stack>
                          </Grid.Col>
                        )}
                      </Grid>
                    </Box>
                  );
                })}
                {/* Bloque Facultad*/}
                {isFacultyUnit && (() => {
                  const codUnidad = `${normalize(activePermission?.permiso?.EntityId)}|ALL`
                  const evsUnidad = evidencesByUG[codUnidad] || [];
                  
                  return (
                    <Box className="container-pink">
                      <Grid>
                        <Grid.Col span={3}>
                          <Text size="xs" c="#E72166" fw={700}>Facultad</Text>
                          <Text size="sm" fw={700}>{activePermission?.permiso?.descripcion?.toUpperCase()}</Text>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          {(kpis ?? []).map((kpi) => {
                            const kpiId = normalize(kpi.Id);
                            const evsForKpi = evsUnidad.filter(ev =>
                              (ev.kpis ?? []).some(x => normalize(x.Id) === kpiId)
                            );
                            return (
                              <div key={kpi.Id} style={{ marginBottom: 5 }}>
                                <Text fw={500}>{kpi.numKpi}.{kpi.titulo}</Text>
                                <Group gap="xs">
                                  {evsForKpi.length === 0 ? (
                                    <Text size="sm" c="dimmed">Sin evidencias cargadas</Text>
                                  ) : (
                                    evsForKpi.map((ev) => (
                                      <Tooltip label={ev.titulo} position="bottom" withArrow key={ev.Id}>
                                        <ActionIcon
                                          variant="filled"
                                          aria-label="file"
                                          radius="xl"
                                          onClick={() => { setSelectedEvidence(ev); setEvidenceModalOpened(true); }}
                                          color="#E72166"
                                        >
                                          <IconFileText style={{ height: '70%', width: '70%' }} />
                                        </ActionIcon>
                                      </Tooltip>
                                    ))
                                  )}
                                </Group>
                              </div>
                            );
                          })}
                        </Grid.Col>
                      </Grid>
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Box>
          {/* CUALIDADES */}
          <Box flex={4} pl={"10px"} className="sticky-box">
            <TitleWithDescription
              title={"Debilidades y Fortalezas"}
              description={`Enunciar fortalezas y debilidades asociadas al criterio de calidad correspondiente.`}
              titleOrder={2}
            />
            {editionEnabled && (
              <>
                <Group mt={"lg"} grow>
                  <Button
                    variant={equals(attribute.tipo,ATTRIBUTE_TYPES.FORTALEZA) ? "filled" : "outline"}
                    color="green"
                    leftSection={<IconTrendingUp size={20} stroke={1.5} />}
                    onClick={() => toggleAttributeType(ATTRIBUTE_TYPES.FORTALEZA)}
                  >
                    Fortaleza
                  </Button>
                  <Button
                    variant={equals(attribute.tipo,ATTRIBUTE_TYPES.DEBILIDAD) ? "filled" : "outline"}
                    color="red"
                    leftSection={<IconTrendingDown size={20} stroke={1.5} />}
                    onClick={() => toggleAttributeType(ATTRIBUTE_TYPES.DEBILIDAD)}
                  >
                    Debilidad
                  </Button>
                </Group>
                <Collapse in={attribute.tipo} mt={'lg'}>
                  <Flex gap="xs" align="flex-start" direction="row" wrap="nowrap">
                    <Box style={{ flex: 1 }}>
                      <div>
                        <TextInput
                          placeholder={`Escribe un enunciado `}
                          maxLength={150}
                          value={attribute.titulo}
                          onChange={(e) => setAttributeField("titulo", e.target.value)}
                        />
                        <Text size="xs" ta={'end'} c={'dimmed'}>{attribute.titulo.length}/150</Text>
                      </div>
                      <div>
                        <Textarea
                          placeholder={`Escribe una justificación (explicaciones y descripciones de causas que justifican la cualidad identificada) `}
                          autosize
                          minRows={1}
                          maxRows={3}
                          maxLength={350}
                          value={attribute.descripcion}
                          onChange={(e) => setAttributeField("descripcion", e.target.value)}
                        />
                        <Text size="xs" ta={'end'} c={'dimmed'}>{attribute.descripcion.length}/350</Text>
                      </div>
                    </Box>
                    <ActionIcon
                      color={attribute.tipo ? (equals(attribute.tipo, ATTRIBUTE_TYPES.FORTALEZA) ? "green" : "red") : "white"}
                      size="lg"
                      onClick={handleSaveAttribute}
                      loading={attributes.saving}
                      justify="flex-start"
                    >
                      <IconCircleArrowRight size={20} />
                    </ActionIcon>
                  </Flex>
                </Collapse>
              </>
            )}
            <Box >
              <ScrollArea.Autosize h={"70vh"} mx="auto" offsetScrollbars>
              {!isReady ? (
                <Text align="center" c="gray" mt={"lg"}>
                  Cargando atributos...
                </Text>
              ) : evaluation?.cualidades?.length === 0 ? (
                <Text align="center" c="gray" mt={"lg"}>
                  No hay fortalezas ni debilidades registradas
                </Text>
              ) : (
                evaluation?.cualidades?.sort((a, b) => new Date(b.dt_inserttime) - new Date(a.dt_inserttime))
                  .map((attr) => (
                    <Group
                      key={attr?.Id}
                      mt="xs"
                      p="xs"
                      wrap="nowrap"
                      bg={attr?.tipoAtributo.nombre === "Fortaleza" ? "#E6F4EA" : "#FEEAEA"}
                      style={{ borderRadius: "8px" }}
                    >
                      {attr?.tipoAtributo.nombre === "Fortaleza" ? (
                        <IconTrendingUp color="green" size={20} />
                      ) : (
                        <IconTrendingDown color="red" size={20} />
                      )}
                      <Box style={{ flex: 1 }}>
                        <Text size="sm" fw={500} >{attr?.titulo}</Text>
                        <Text size="xs">{attr?.descripcion}</Text>
                      </Box>
                      {!attr.asignadoPlanMejora && editionEnabled && (
                        <ActionIcon color="gray" size="sm" variant="light" onClick={() => handleDeleteAttribute(attr.Id)}>
                          <IconX size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  ))
              )}
              </ScrollArea.Autosize>
            </Box>
          </Box>
        </Flex>
        <ModalAssessment 
          opened={assessmentOpened} 
          onClose={handleCloseAssessmentModal}
          assessmentData={assessmentDataSelected}
          onSave={handleSaveAssessment}
          onDelete={handleDeleteAssessment}
        />
        <ModalEvidence
          opened={evidenceModalOpened}
          onClose={() => setEvidenceModalOpened(false)}
          evidence={selectedEvidence}
        />
      </Box>
    </Box>
  );
}
export default AssessmentPage;